import { JSX } from 'react';
import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { ApplicationState } from '../../../store';

export type BreadcrumbEntry = {
  name: string;
  path: string;
};

interface Props {
  children?: JSX.Element;
  applicantName?: string;
  pageName?: string;
  showProgrammesCrumb?: boolean;
  breadcrumbs?: BreadcrumbEntry[];
}

function ApplicantHeader(props: Props): JSX.Element {
  const params = useParams();
  const { userId, applicationId, uni } = params;
  const { application, applicant } = useSelector((state: ApplicationState) => state.apply);

  let fullName = '';
  if (application?.givenName) {
    fullName = `${application.givenName} ${application.familyName ?? ''}`;
  } else if (applicant?.givenName) {
    fullName = `${applicant.givenName} ${applicant.familyName ?? ''}`;
  } else {
    fullName = applicant?.knownBy ?? '';
  }
  const { children, pageName, applicantName = fullName, showProgrammesCrumb, breadcrumbs } = props;

  const backToStudentListUrl = ((): string => {
    try {
      const backUrl = sessionStorage.getItem('cas-staff-uni-backUrl') ?? '/';
      return backUrl !== '/' ? `/${backUrl}` : '/admissions';
    } catch {
      return '/admissions';
    }
  })();

  const handleBackToStudentListClick = (): void => {
    try {
      sessionStorage.removeItem('cas-staff-uni-backUrl');
    } catch {
      // Ignore session storage errors
    }
  };

  return (
    <Box as="header" mb={2}>
      <Breadcrumb mb={4} spacing="2" separator={<FontAwesomeIcon className="utility-icon" icon={faChevronRight} />}>
        <BreadcrumbItem>
          <BreadcrumbLink
            as={RouterLink}
            to={backToStudentListUrl}
            onClick={handleBackToStudentListClick}
            id="applicantHeader-closeButton"
          >
            Applicants
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink
            as={RouterLink}
            to={`/application/${uni}/${userId}/${applicationId}/summary`}
            data-selector="studentHeader-studentNo"
          >
            {applicantName}
          </BreadcrumbLink>
        </BreadcrumbItem>
        {showProgrammesCrumb && (
          <BreadcrumbItem>
            <BreadcrumbLink
              as={RouterLink}
              to={`/application/${uni}/${userId}/${applicationId}/programmes`}
              data-selector="studentHeader-studentNo"
            >
              Programmes
            </BreadcrumbLink>
          </BreadcrumbItem>
        )}
        {breadcrumbs?.map(entry => (
          <BreadcrumbItem key={entry.path}>
            <BreadcrumbLink as={RouterLink} to={entry.path} data-selector="studentHeader-studentNo">
              {entry.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
        ))}
        {pageName && (
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink data-selector="studentHeader-studentNo">{pageName}</BreadcrumbLink>
          </BreadcrumbItem>
        )}
      </Breadcrumb>
      {children}
    </Box>
  );
}

export default ApplicantHeader;
